import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { App, Checkbox, Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import { createApp } from '@/api/apps'
import { ApiError } from '@/api/client'
import BrandButton from '@/components/BrandButton'
import { useAppStore } from '@/store'
import { confirmDisplayName } from '@/lib/appDisplayName'
import { rememberCreateSpaceLock } from '@/lib/createSpaceLock'
import { placeholderApp } from '@/store/app/appsSlice'
import { APP_NAME_MAX, canSubmitCreateApp } from './createAppForm'

interface CreateAppModalProps {
  open: boolean
  /** 空间后台入口的来源空间。创建成功后，首版固定信息锁定为空间自建 */
  lockSpaceId?: string | null
  onClose: () => void
}

function CreateAppModal({ open, lockSpaceId, onClose }: CreateAppModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const navigate = useNavigate()
  const upsertApp = useAppStore((s) => s.upsertApp)
  const [name, setName] = useState('')
  const [appId, setAppId] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setName('')
    setAppId('')
    setAgreed(false)
    onClose()
  }

  const close = () => {
    if (submitting) return
    reset()
  }

  const canCreate = canSubmitCreateApp({ name, appId, agreed })

  const submit = async () => {
    if (!canCreate || submitting) return
    setSubmitting(true)
    try {
      const created = await createApp({ name, id: appId })
      upsertApp({
        ...placeholderApp(created.app_id, {
          name: created.name,
          module_id: created.module_id,
        }),
        app_id: created.app_id,
        app_secret: created.app_secret || 'sk_****************',
      })
      confirmDisplayName(created.app_id, created.name)
      if (lockSpaceId) rememberCreateSpaceLock(created.app_id, lockSpaceId)
      message.success(t('workbench.createSuccess'))
      reset()
      navigate(`/apps/${created.app_id}/credentials`)
    } catch (err) {
      message.error(
        err instanceof ApiError && err.message
          ? err.message
          : t('workbench.createFailed'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      closable={false}
      width={614}
      centered
      destroyOnHidden
      styles={{
        container: {
          padding: 0,
          overflow: 'hidden',
          borderRadius: 14,
          border: '1px solid #DCDCDC',
          background: 'rgba(255, 255, 255, 0.85)',
          boxShadow: '0px 16px 22px -6px rgba(28, 28, 28, 0.16)',
          backdropFilter: 'blur(20px)',
        },
        body: { padding: 0 },
      }}
    >
      <div className="flex items-center justify-between border-b border-black/[0.04] py-5 pl-5 pr-5">
        <h2 className="m-0 text-lg font-medium leading-[25px] text-black">
          {t('workbench.createTitle')}
        </h2>
        <div className="flex items-center gap-5">
          <span aria-hidden className="h-[30px] w-px bg-black/[0.04]" />
          <button
            type="button"
            aria-label={t('workbench.createClose')}
            onClick={close}
            className="flex size-5 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-black"
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>
      </div>

      <div className="flex flex-col px-5 pb-5 pt-2.5">
        <div className="flex flex-col gap-5 rounded-lg border border-[#DCDCDC] bg-white/85 p-5">
          <label className="flex flex-col gap-1.5">
            <span className="flex items-center text-[13px] leading-5 text-black/60">
              {t('workbench.nameLabel')}
              <span className="ml-0.5 text-[#E53E3E]">*</span>
            </span>
            <input
              value={name}
              maxLength={APP_NAME_MAX}
              onChange={(e) => setName(e.target.value)}
              className="h-[38px] rounded-lg border border-[#DCDCDC] bg-white/85 px-3.5 text-[13px] leading-5 text-black outline-none placeholder:text-black/40"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="flex items-center text-[13px] leading-5 text-black/60">
              {t('workbench.createAppIdLabel')}
              <span className="ml-0.5 text-[#E53E3E]">*</span>
            </span>
            <input
              value={appId}
              maxLength={32}
              placeholder={t('workbench.createAppIdPlaceholder')}
              onChange={(e) => setAppId(e.target.value)}
              className="h-[38px] rounded-lg border border-[#DCDCDC] bg-white/85 px-3.5 text-[13px] leading-5 text-black outline-none placeholder:text-black/40"
            />
            <span className="text-[13px] leading-5 text-black/60">
              {t('workbench.createAppIdHint')}
            </span>
          </label>

          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="!text-[13px] !leading-5 !text-black"
          >
            {t('workbench.createAgreement')}
          </Checkbox>
        </div>
      </div>

      <div className="flex justify-end gap-2.5 px-5 pb-5">
        <BrandButton variant="outline" onClick={close}>
          {t('common.cancel')}
        </BrandButton>
        <BrandButton
          disabled={!canCreate}
          loading={submitting}
          style={{ padding: '0 24px' }}
          onClick={() => {
            void submit()
          }}
        >
          {t('workbench.createSubmit')}
        </BrandButton>
      </div>
    </Modal>
  )
}

export default CreateAppModal
